import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { catchError, finalize, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Taxon } from '../domain/Analyse/taxon';
import { WaterProcess } from '../domain/EAU/water-process';
import { IBDProcess } from '../domain/IBD/IDB-process';
import { IBGNProcess } from '../domain/IBGN/IBGN-process';
import { IBMRProcess } from '../domain/IBMR/IBMR-process';
import { IMLProcess } from '../domain/IML/IML-process';
import { LacProcess } from '../domain/Lac - EAU/lac-process';
import { MGCEProcess } from '../domain/MGCE/MGCE-Process';
import { Process } from '../domain/MPCE/process';
import { Document } from '../domain/Shared/document';
import { EInterventionType } from '../domain/planning/enum/intervention-type';
import { Intervention } from '../domain/planning/intervention';
import { SampleTakingModel } from '../models/new-substrat-model';
import { CoreData } from '../tools/core-data';
import { LoadingTask } from '../tools/loading-task';
import { InterventionOfflineUpdateDto } from './DTO/intervention-offline-update.dto';
import { InterventionUpdateDTO } from './DTO/intervention-update.dto';
import { BaseService } from './base-service';
import { InterventionCreateDTO } from './DTO/intervention.dto';

@Injectable({
    providedIn: 'root'
})
export class InterventionService extends BaseService {
    private serviceUrl = `${environment.serviceMainBaseUrl}api/intervention`;
    constructor(
        protected http: HttpClient,
        protected coreData: CoreData,
        protected loadingTask: LoadingTask,
        protected snackBare: MatSnackBar,
        protected router: Router,
    ) {
        super(coreData, router, snackBare);
    }

    getAllIntervention() {
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/all`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getAllIntervention')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: Intervention[]) => {
                return response.map((item: Intervention) => {
                    return Intervention.mapFromJSON(item);
                });
            })
        );
    }

    getInterventionBySampler() {
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/BySampler`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getInterventionBySampler')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: Intervention[]) => {
                if (response) {
                    return response.map((item: Intervention) => {
                        return Intervention.mapFromJSON(item);
                    });
                } else {
                    return null;
                }
            })
        );
    }

    getAllActiveIntervention() {
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/Active`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getAllActiveIntervention')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: Intervention[]) => {
                if (response) {
                    return response.map((item: Intervention) => {
                        return Intervention.mapFromJSON(item);
                    });
                } else {
                    return null;
                }
            })
        );
    }

    getInterventionDetails(code: string) {
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/${code}/detail`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getInterventionDetails')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: Intervention) => {
                return Intervention.mapFromJSON(response);
            })
        );
    }

    // createIntervention(typeIntervention: EInterventionType, sampleCode: string) {
    //     this.loadingTask.activateLoading();
    //     const dto = new InterventionCreateDTO();
    //     dto.Type = typeIntervention;
    //     dto.SampleCode = sampleCode;
    //     // const body = { type: typeIntervention };
    //     return this.http.post(`${this.serviceUrl}`, dto, this.generateHeader()).pipe(
    //         catchError(this.handleError<any>('createNewIntervention')),
    //         finalize(() => this.loadingTask.deactivateLoading()),
    //         map(x => x));
    // }
    createIntervention(dto: InterventionCreateDTO) {
        this.loadingTask.activateLoading();
        // const dto = new InterventionCreateDTO();
        // dto.Type = typeIntervention;
        // dto.SampleCode = sampleCode;
        // const body = { type: typeIntervention };
        return this.http.post(`${this.serviceUrl}`, dto, this.generateHeader()).pipe(
            catchError(this.handleError<any>('createNewIntervention')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: Intervention) => {
                return Intervention.mapFromJSON(response);
            }));
    }
    
    update(intervention: Intervention) {
        this.loadingTask.activateLoading();

        const dto = new InterventionUpdateDTO();
        Object.assign(dto, intervention);
        dto.ResponsiblePuid = intervention.Responsible.Code;
        dto.SamplerPuid = intervention.Sampler.Code;
        dto.Sampler3Puid = intervention.Sampler3.Code;
        dto.Tracabilite.VerificateurPuid = intervention.Tracabilite?.Verificateur?.Code;

        return this.http.put(`${this.serviceUrl}/${intervention.Code}`, dto, this.generateHeader()).pipe(
            catchError(this.handleError<any>('createNewIntervention')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map(x => x));
    }
    
    updateZombiesToRedemption(interventionCode: string, sampleCode: string) {
        this.loadingTask.activateLoading();

        return this.http.put(`${this.serviceUrl}/${interventionCode}/zombie/${sampleCode}`, null, this.generateHeader()).pipe(
            catchError(this.handleError<any>('updateZombieToRedemption')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map(x => x));
    }

    updateOfflineIntervention(
        intervention: Intervention,
        env: Intervention,
        process: Process | IBGNProcess | WaterProcess | IBDProcess | LacProcess | MGCEProcess | IBMRProcess | IMLProcess,
        substrat: SampleTakingModel[],
        documents: Document[]) {

        this.loadingTask.activateLoading();
        const formData: FormData = new FormData();

        const dto = new InterventionOfflineUpdateDto();
        dto.Intervention = Object.assign(new InterventionUpdateDTO(), intervention);
        dto.Intervention.ResponsiblePuid = intervention.Responsible.Code;
        dto.Intervention.SamplerPuid = intervention.Sampler.Code;
        dto.Intervention.Sampler3Puid = intervention.Sampler3.Code;
        dto.Intervention.Tracabilite.VerificateurPuid = intervention.Tracabilite?.Verificateur?.Code;
        dto.Environment = env;
        switch (intervention.Type) {
            case EInterventionType.MPCE:
                dto.MPCEStation = process as Process;
                dto.MPCESubstrats = substrat;
                break;
            case EInterventionType.IBGN:
                dto.IBGNStation = process as IBGNProcess;
                dto.IBGNSubstrats = substrat;
                break;
            case EInterventionType.IBD:
                dto.IBDProcess = process as IBDProcess;
                break;
            case EInterventionType.EAU:
                dto.WaterProcess = process as WaterProcess;
                break;
            case EInterventionType.LAC:
                dto.LacProcess = process as LacProcess;
                break;
            case EInterventionType.IBMR:
                dto.IBMRProcess = process as IBMRProcess;
                // if (taxonList)
                //     dto.IBMRTaxonList = taxonList;
                break;
            case EInterventionType.MGCE:
                dto.MGCEProcess = process as MGCEProcess;
                break;
            case EInterventionType.IML:
                dto.IMLProcess = process as IMLProcess;
                break;
        }

        documents.forEach(doc => {
            if (!doc.UIDisabled) {
                formData.append('files', doc.File, doc.Name);
                formData.append('documentTypes', `${doc.DocumentType}`);
                formData.append('reason', `${doc.Description}`);
            }
        });

        formData.append('dto', JSON.stringify(dto));
        return this.http.put(`${this.serviceUrl}/${intervention.Code}/Sync`, formData, this.generateHeaderForFormData()).pipe(
            catchError(this.handleError<any>('saveOfflineIntervention')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map(x => x));
    }

    cancel(intervention: Intervention, reason: string) {
        this.loadingTask.activateLoading();
        const body: any = {};
        if (reason) {
            body.reason = reason;
        }
        return this.http.put(`${this.serviceUrl}/${intervention.Code}/cancel`, body, this.generateHeader()).pipe(
            catchError(this.handleError<any>('createNewIntervention')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map(x => x));
    }

    changeCOFRAC(code: string, isCofrac: boolean, reason?: string) {
        this.loadingTask.activateLoading();
        const body: any = {};
        if (reason) {
            body.reason = reason;
        }
        return this.http.put(`${this.serviceUrl}/${code}/cofrac/${isCofrac}`, body, this.generateHeader()).pipe(

            catchError(this.handleError<any>('createNewIntervention')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map(x => x));
    }

    startEndLocalStorageOperation(interventionCode: string, forStart: boolean = true) {
        this.loadingTask.activateLoading();

        return this.http.post(`${this.serviceUrl}/localStorage/${interventionCode}/${forStart ? 'start' : 'end'}`, null, this.generateHeader()).pipe(
            catchError(this.handleError<any>('startLocalStorageOperation')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map(x => x));
    }

    exportIndexedDBAndLocalStorageAsJSON(jsonDB, jsonLocalStorage) {
        const formData: FormData = new FormData();

        formData.append('db', jsonDB);
        formData.append('localStorage', jsonLocalStorage);
        this.loadingTask.activateLoading();
        return this.http.post(`${this.serviceUrl}/dump`, formData, this.generateHeaderForFormData()).pipe(
            catchError(this.handleError<any>('exportIndexedDBAndLocalStorageAsJSON')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map(x => x));
    }
}

